// QR Studio configuration types & presets.
// Keeps the heavy UI file lean by housing the type system + defaults here.

export type DotType =
  | "square"
  | "dots"
  | "rounded"
  | "extra-rounded"
  | "classy"
  | "classy-rounded";

export type CornerSquareType = "square" | "dot" | "extra-rounded";
export type CornerDotType = "square" | "dot";
export type ErrorLevel = "L" | "M" | "Q" | "H";
export type GradientType = "linear" | "radial";

export interface ColorOption {
  mode: "solid" | "gradient";
  color: string;
  gradient: {
    type: GradientType;
    rotation: number; // radians
    colors: [string, string];
  };
}

export type FrameStyle = "box" | "rounded" | "banner";

export interface FrameConfig {
  enabled: boolean;
  style: FrameStyle; // box (square), rounded, or banner (just a CTA bar below)
  text: string; // call-to-action, e.g. "SCAN ME"
  color: string; // frame + CTA-bar color
  textColor: string; // CTA text color
}

export interface QRConfig {
  // Modules (dots)
  dot: ColorOption & { style: DotType };
  // Corner squares (the 3 big squares)
  cornerSquare: ColorOption & { style: CornerSquareType };
  // Corner dots (inner of corner squares)
  cornerDot: ColorOption & { style: CornerDotType };
  // Background
  background: ColorOption;
  // Logo
  logo: {
    src: string | null;
    size: number; // 0–0.5
    margin: number; // px
    hideBackgroundDots: boolean;
  };
  // Frame + call-to-action
  frame: FrameConfig;
  // Advanced
  size: number;
  margin: number;
  errorCorrection: ErrorLevel;
}

const solid = (color: string): ColorOption => ({
  mode: "solid",
  color,
  gradient: {
    type: "linear",
    rotation: 0,
    colors: [color, color],
  },
});

const grad = (
  type: GradientType,
  rotation: number,
  c1: string,
  c2: string,
): ColorOption => ({
  mode: "gradient",
  color: c1,
  gradient: { type, rotation, colors: [c1, c2] },
});

// Plain, classic QR: black square modules on white. Customization layers on
// top of this baseline only when the user has Pro+ unlocked.
export const DEFAULT_CONFIG: QRConfig = {
  dot: { ...solid("#000000"), style: "square" },
  cornerSquare: { ...solid("#000000"), style: "square" },
  cornerDot: { ...solid("#000000"), style: "square" },
  background: solid("#ffffff"),
  logo: {
    src: null,
    size: 0.3,
    margin: 6,
    hideBackgroundDots: true,
  },
  frame: {
    enabled: false,
    style: "rounded",
    text: "SCAN ME",
    color: "#6366f1",
    textColor: "#ffffff",
  },
  size: 320,
  margin: 0,
  errorCorrection: "Q",
};

export interface Preset {
  name: string;
  description: string;
  thumbColor: string;
  config: Partial<QRConfig> & Record<string, any>;
}

// Curated style presets — each one applies a coherent look in one click.
export const PRESETS: Preset[] = [
  {
    name: "Classic",
    description: "Black squares, white background",
    thumbColor: "#18181b",
    config: {
      dot: { ...solid("#18181b"), style: "square" },
      cornerSquare: { ...solid("#18181b"), style: "square" },
      cornerDot: { ...solid("#18181b"), style: "square" },
      background: solid("#ffffff"),
    },
  },
  {
    name: "Soft",
    description: "Rounded dark, white background",
    thumbColor: "#27272a",
    config: {
      dot: { ...solid("#27272a"), style: "rounded" },
      cornerSquare: { ...solid("#27272a"), style: "extra-rounded" },
      cornerDot: { ...solid("#27272a"), style: "dot" },
      background: solid("#ffffff"),
    },
  },
  {
    name: "Brand",
    description: "Indigo→violet gradient",
    thumbColor: "#6366f1",
    config: {
      dot: { ...grad("linear", 0, "#6366f1", "#a855f7"), style: "rounded" },
      cornerSquare: { ...solid("#4f46e5"), style: "extra-rounded" },
      cornerDot: { ...solid("#7c3aed"), style: "dot" },
      background: solid("#ffffff"),
    },
  },
  {
    name: "Sunset",
    description: "Warm rose→amber gradient",
    thumbColor: "#e11d48",
    config: {
      dot: { ...grad("linear", Math.PI / 4, "#e11d48", "#f59e0b"), style: "rounded" },
      cornerSquare: { ...solid("#9f1239"), style: "extra-rounded" },
      cornerDot: { ...solid("#d97706"), style: "dot" },
      background: solid("#fffbeb"),
    },
  },
  {
    name: "Forest",
    description: "Emerald radial",
    thumbColor: "#059669",
    config: {
      dot: { ...grad("radial", 0, "#059669", "#0d9488"), style: "extra-rounded" },
      cornerSquare: { ...solid("#065f46"), style: "extra-rounded" },
      cornerDot: { ...solid("#10b981"), style: "dot" },
      background: solid("#ecfdf5"),
    },
  },
  {
    name: "Inverted",
    description: "White on ink",
    thumbColor: "#ffffff",
    config: {
      dot: { ...solid("#ffffff"), style: "rounded" },
      cornerSquare: { ...solid("#ffffff"), style: "extra-rounded" },
      cornerDot: { ...solid("#ffffff"), style: "dot" },
      background: solid("#18181b"),
    },
  },
  {
    name: "Dots",
    description: "Tiny round dots",
    thumbColor: "#18181b",
    config: {
      dot: { ...solid("#18181b"), style: "dots" },
      cornerSquare: { ...solid("#18181b"), style: "dot" },
      cornerDot: { ...solid("#18181b"), style: "dot" },
      background: solid("#ffffff"),
    },
  },
  {
    name: "Ocean",
    description: "Sky→indigo angled",
    thumbColor: "#0ea5e9",
    config: {
      dot: { ...grad("linear", Math.PI / 2, "#0ea5e9", "#4f46e5"), style: "classy-rounded" },
      cornerSquare: { ...solid("#0369a1"), style: "extra-rounded" },
      cornerDot: { ...solid("#4338ca"), style: "dot" },
      background: solid("#f0f9ff"),
    },
  },
];
